import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ordensApi, colaboradoresApi, cidadesApi } from '../lib/api';
import {
  formatCurrency,
  formatDate,
  formatEndereco,
  formatOrdemNumero,
  getStatusColor,
  STATUS_OPTIONS,
  copyToClipboard,
  generateWhatsAppMessageGradeDia
} from '../lib/utils';
import { CidadeSelectOptions, getZonasDaCidade } from '../components/CidadeSelectOptions';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Calendar, User, MapPin, Clock, DollarSign, MessageCircle } from 'lucide-react';

const todayBrasilia = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

const GradeOperacional = () => {
  const [ordens, setOrdens] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [cidadesList, setCidadesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateMode, setDateMode] = useState('dia'); // 'dia' | 'intervalo'
  const [dataUnica, setDataUnica] = useState(todayBrasilia());
  const [dataInicio, setDataInicio] = useState(todayBrasilia());
  const [dataFim, setDataFim] = useState(todayBrasilia());
  const [colaboradorFilter, setColaboradorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cidadeFilter, setCidadeFilter] = useState('all');
  const [zonaFilter, setZonaFilter] = useState('all');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const extra = {};
      if (colaboradorFilter !== 'all') extra.colaborador_id = colaboradorFilter;
      if (statusFilter !== 'all') extra.status = statusFilter;
      if (cidadeFilter !== 'all') extra.cidade = cidadeFilter;
      if (zonaFilter !== 'all') extra.zona = zonaFilter;

      let ordensPromise;
      if (dateMode === 'dia') {
        ordensPromise = ordensApi.list('dia', dataUnica, extra);
      } else {
        ordensPromise = ordensApi.list('all', null, { ...extra, data_inicio: dataInicio, data_fim: dataFim });
      }

      const [ordensRes, colabRes, cidadesRes] = await Promise.all([
        ordensPromise,
        colaboradoresApi.list(),
        cidadesApi.list()
      ]);
      setOrdens(ordensRes.data);
      setColaboradores(colabRes.data.filter((c) => c.ativo !== false));
      setCidadesList(cidadesRes.data);
    } catch (error) {
      console.error('Error loading grade:', error);
      toast.error('Erro ao carregar grade operacional');
    } finally {
      setLoading(false);
    }
  }, [dateMode, dataUnica, dataInicio, dataFim, colaboradorFilter, statusFilter, cidadeFilter, zonaFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Group ordens by colaborador, then by data
  const grouped = useMemo(() => {
    const byColab = {};
    ordens.forEach((o) => {
      const key = o.colaborador_id || 'sem_colaborador';
      if (!byColab[key]) {
        byColab[key] = {
          colaborador_id: o.colaborador_id,
          colaborador_nome: o.colaborador_nome || 'Sem colaborador',
          colaborador_telefone: o.colaborador_telefone || '',
          ordens: [],
          totalValor: 0
        };
      }
      byColab[key].ordens.push(o);
      byColab[key].totalValor += Number(o.valor || 0);
    });
    // Sort ordens by data + hora
    Object.values(byColab).forEach((c) => {
      c.ordens.sort((a, b) => {
        const d = (a.data_agendamento || '').localeCompare(b.data_agendamento || '');
        if (d !== 0) return d;
        return (a.hora_agendamento || '').localeCompare(b.hora_agendamento || '');
      });
    });
    // Sort colaboradores by name
    return Object.values(byColab).sort((a, b) =>
      (a.colaborador_nome || '').localeCompare(b.colaborador_nome || '')
    );
  }, [ordens]);

  const handleCopyGrade = async (col) => {
    // For "grade do dia" — only include ordens on dataUnica (or first day of range)
    const targetDate = dateMode === 'dia' ? dataUnica : dataInicio;
    const ordensDoDia = col.ordens.filter((o) => o.data_agendamento === targetDate);
    if (ordensDoDia.length === 0) {
      toast.error(`Nenhuma OS de ${col.colaborador_nome} em ${formatDate(targetDate)}`);
      return;
    }
    // Fallback: se a OS não tem valor_prestador, calcula pela comissão do colaborador
    const colab = colaboradores.find((c) => c.id === col.colaborador_id);
    const pct = colab ? Number(colab.comissao || 0) : 0;
    const ordensComVp = ordensDoDia.map((o) => {
      const vpSet = o.valor_prestador !== undefined && o.valor_prestador !== null && Number(o.valor_prestador) > 0;
      const vp = vpSet
        ? Number(o.valor_prestador)
        : (pct > 0 ? Number(((Number(o.valor || 0) * pct) / 100).toFixed(2)) : null);
      return { ...o, valor_prestador: vp };
    });
    const msg = generateWhatsAppMessageGradeDia(col.colaborador_nome, ordensComVp, targetDate);
    const ok = await copyToClipboard(msg);
    if (ok) toast.success(`Grade do dia de ${col.colaborador_nome} copiada!`);
    else toast.error('Erro ao copiar mensagem');
  };

  return (
    <div className="space-y-6" data-testid="grade-operacional-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Grade Operacional
          </h1>
          <p className="text-gray-500 mt-1">
            Agenda por colaborador — visualize e envie as OS do dia
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <Tabs value={dateMode} onValueChange={setDateMode}>
            <TabsList data-testid="date-mode-tabs">
              <TabsTrigger value="dia" data-testid="date-mode-dia">Dia único</TabsTrigger>
              <TabsTrigger value="intervalo" data-testid="date-mode-intervalo">Intervalo</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {dateMode === 'dia' ? (
              <div className="space-y-1">
                <label className="text-xs text-gray-500">Data</label>
                <input
                  type="date"
                  value={dataUnica}
                  onChange={(e) => setDataUnica(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  data-testid="data-unica-input"
                />
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">De</label>
                  <input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    data-testid="data-inicio-input"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Até</label>
                  <input
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    data-testid="data-fim-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-1">
              <label className="text-xs text-gray-500">Colaborador</label>
              <Select value={colaboradorFilter} onValueChange={setColaboradorFilter}>
                <SelectTrigger data-testid="filter-colaborador"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {colaboradores.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-gray-500">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-gray-500">Cidade</label>
              <Select value={cidadeFilter} onValueChange={(v) => { setCidadeFilter(v); setZonaFilter('all'); }}>
                <SelectTrigger data-testid="filter-cidade"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <CidadeSelectOptions cidades={cidadesList} currentValue={cidadeFilter !== 'all' ? cidadeFilter : ''} />
                </SelectContent>
              </Select>
            </div>

            {cidadeFilter !== 'all' && getZonasDaCidade(cidadesList, cidadeFilter).length > 0 && (
              <div className="space-y-1">
                <label className="text-xs text-gray-500">Zona/Região</label>
                <Select value={zonaFilter} onValueChange={setZonaFilter}>
                  <SelectTrigger data-testid="filter-zona"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {getZonasDaCidade(cidadesList, cidadeFilter).map((z) => (
                      <SelectItem key={z.nome} value={z.nome}>{z.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Kanban */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-gray-500">
            <Calendar size={40} className="mx-auto mb-3 text-gray-300" />
            Nenhuma OS encontrada com os filtros selecionados
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="kanban-container">
          {grouped.map((col) => (
            <Card
              key={col.colaborador_id || 'sem'}
              className="border border-gray-200"
              data-testid={`kanban-col-${col.colaborador_id || 'sem'}`}
            >
              {/* Column header */}
              <div className="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <User size={16} className="text-gray-400 flex-shrink-0" />
                    <h3 className="font-semibold text-base truncate" title={col.colaborador_nome}>
                      {col.colaborador_nome}
                    </h3>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                    <span>{col.ordens.length} OS</span>
                    <span>•</span>
                    <span className="font-medium text-emerald-700">
                      {formatCurrency(col.totalValor)}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopyGrade(col)}
                  title="Copiar agenda do dia"
                  className="flex-shrink-0"
                  data-testid={`copy-grade-${col.colaborador_id || 'sem'}`}
                >
                  <MessageCircle size={14} className="mr-1 text-green-600" />
                  Copiar agenda do dia
                </Button>
              </div>

              {/* Cards grid (responsive) */}
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {col.ordens.map((o) => (
                  <Card
                    key={o.id}
                    className="bg-white shadow-sm hover:shadow-md transition-shadow"
                    data-testid={`os-card-${o.id}`}
                  >
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs font-mono text-gray-500">
                          {o.numero && (
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 border font-semibold text-gray-700">
                              {formatOrdemNumero(o.numero)}
                            </span>
                          )}
                        </div>
                        <Badge
                          style={{ backgroundColor: getStatusColor(o.status), color: 'white' }}
                          className="text-[10px] px-2 py-0"
                        >
                          {o.status}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                        <Clock size={12} />
                        <span className="font-medium">
                          {formatDate(o.data_agendamento)} · {o.hora_agendamento || '--:--'}
                        </span>
                        {o.periodo_atendimento && (
                          <span className="text-gray-400">({o.periodo_atendimento})</span>
                        )}
                      </div>

                      <div className="text-sm font-semibold text-gray-800 break-words">
                        {o.servico_nome}
                      </div>

                      <div className="text-sm text-gray-700">
                        <div className="flex items-center gap-1">
                          <User size={12} className="text-gray-400 flex-shrink-0" />
                          <span className="break-words">{o.cliente_nome}</span>
                        </div>
                        {o.cliente_telefone && (
                          <div className="text-xs text-gray-500 ml-4">
                            {o.cliente_telefone}
                          </div>
                        )}
                      </div>

                      {formatEndereco(o.cliente_endereco) && (
                        <div className="flex items-start gap-1 text-xs text-gray-500">
                          <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2 break-words">
                            {formatEndereco(o.cliente_endereco)}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-1 border-t flex-wrap gap-1">
                        <div className="flex items-center gap-1 text-sm font-semibold text-emerald-700">
                          <DollarSign size={12} />
                          {formatCurrency(o.valor)}
                        </div>
                        {o.cidade_atendimento && (
                          <span className="text-[10px] text-gray-600 font-medium uppercase tracking-wide">
                            {[o.cidade_atendimento, o.zona_atendimento, o.bairro_atendimento].filter(Boolean).join(' • ')}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default GradeOperacional;
